import pLimit from "p-limit";
import pThrottle from "p-throttle";

/**
 * Options for the runConcurrently function
 */
export type RunConcurrentlyOptions<T, R> = {
  /** An array of items to be processed */
  items: T[];
  /** An async function to perform work on each item */
  task: (item: T, index: number) => Promise<R>;
  /** The maximum number of concurrent executions allowed */
  maxConcurrency: number;
  /** The maximum number of invocations within an interval. Uses `maxConcurrency` if not provided */
  maxThrottleInvocationsPerInterval?: number;
  /** The timespan for `maxThrottleInvocationsPerInterval` in milliseconds */
  throttleIntervalMs?: number;
  /** Whether to return all settled results instead of throwing on first rejection */
  allSettled?: boolean;
};

// Update overloads to use the interface
export async function runConcurrently<T, R>(
  options: RunConcurrentlyOptions<T, R> & {
    allSettled?: true;
  }
): Promise<ConcurrencyResults<T, R>>;

export async function runConcurrently<T, R>(
  options: RunConcurrentlyOptions<T, R> & {
    allSettled?: false;
  }
): Promise<R[]>;

/**
 * Executes a list of tasks concurrently, limiting the maximum number of simultaneous executions.
 *
 * @param options - Configuration options for concurrent execution
 * @param options.items - An array of items to be processed
 * @param options.task - An async function to perform work on each item
 * @param options.maxConcurrency - The maximum number of concurrent executions allowed
 * @param options.maxThrottleInvocationsPerInterval - The maximum number of invocations within an interval. Uses `maxConcurrency` if not provided
 * @param options.throttleIntervalMs - The timespan for `maxThrottleInvocationsPerInterval` in milliseconds
 * @param options.allSettled - Whether to return all settled results instead of throwing on first rejection
 * @returns A Promise that resolves to an array of results from each task
 *
 * @example
 * // Sample array of "items" (you can replace these with your real data)
 * const items = [1, 2, 3, 4, 5];
 *
 * async function processItem(item: number, index: number): Promise<string> {
 *   // Simulate some asynchronous work with a delay
 *   return new Promise((resolve) => setTimeout(() => resolve(`Processed ${item}`), 1000));
 * }
 *
 * const results = await runConcurrently({
 *   items,
 *   task: processItem,
 *   maxConcurrency: 10
 * });
 *
 * console.log('All tasks completed:', results);
 */
export async function runConcurrently<T, R>({
  items,
  task,
  maxConcurrency,
  maxThrottleInvocationsPerInterval,
  throttleIntervalMs,
  allSettled = true,
}: RunConcurrentlyOptions<T, R>): Promise<R[] | ConcurrencyResults<T, R>> {
  let promises = [];
  const limit = pLimit(maxConcurrency);

  // Wrap the task with error logging
  const taskWithErrorLogging = async (item: T, index: number): Promise<R> => {
    try {
      return await task(item, index);
    } catch (error) {
      console.error(`runConcurrently: Task failed at index ${index}:`, error);
      throw error;
    }
  };

  if (throttleIntervalMs !== undefined) {
    if (Number.isNaN(throttleIntervalMs)) {
      throw new Error("throttleIntervalMs must be a number");
    }

    if (throttleIntervalMs <= 0) {
      throw new Error("throttleIntervalMs must be greater than 0");
    }

    const throttle = pThrottle({
      limit: maxThrottleInvocationsPerInterval || maxConcurrency,
      interval: throttleIntervalMs,
    });
    const throttledTask = throttle((item, index) => {
      return limit(() => taskWithErrorLogging(item, index));
    });
    promises = items.map((item, index) => throttledTask(item, index));
  } else {
    promises = items.map((item, index) => limit(() => taskWithErrorLogging(item, index)));
  }
  if (allSettled) {
    const results = await Promise.allSettled(promises);
    return parseSettledResults<T, R>(items, results);
  } else {
    return await Promise.all(promises);
  }
}
export type ConcurrencyResults<T, R> = {
  totalSuccesses: number;
  totalFailures: number;
  results: R[];
  failures: Array<{
    item: T;
    index: number;
    error: Error;
  }>;
  raw: PromiseSettledResult<R>[];
};

export function parseSettledResults<T, R>(
  items: T[],
  settledResults: PromiseSettledResult<R>[]
): ConcurrencyResults<T, R> {
  const successes: R[] = [];
  const failures: Array<{ item: T; index: number; error: Error }> = [];

  settledResults.forEach((result, index) => {
    if (result.status === "fulfilled") {
      successes.push(result.value);
    } else {
      failures.push({
        item: items[index],
        index,
        error: result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
      });
    }
  });
  return {
    totalSuccesses: successes.length,
    totalFailures: failures.length,
    results: successes,
    failures,
    raw: settledResults,
  };
}

/**
 * Options for the runConcurrentlyStream function
 */
export type RunConcurrentlyStreamOptions<T, R> = {
  /** An async iterable that yields items to be processed */
  source: AsyncIterable<T>;
  /** An async function to perform work on each item */
  task: (item: T, index: number) => Promise<R>;
  /** The maximum number of concurrent executions allowed */
  maxConcurrency: number;
};

export type StreamResult<T, R> =
  | {
      status: "fulfilled";
      value: R;
      item: T;
      index: number;
    }
  | {
      status: "rejected";
      reason: Error;
      item: T;
      index: number;
    };

/**
 * Processes items from an async iterable with concurrency control, yielding results as they complete.
 * Unlike runConcurrently, this function doesn't collect all items upfront - it pulls items from the
 * source as processing slots become available, providing true streaming behavior.
 *
 * @param options - Configuration options for concurrent streaming execution
 * @returns An async iterable that yields results as they complete
 *
 * @example
 * async function* generateItems() {
 *   for (let i = 0; i < 100; i++) {
 *     yield i;
 *   }
 * }
 *
 * async function processItem(item: number): Promise<string> {
 *   await new Promise(resolve => setTimeout(resolve, 100));
 *   return `Processed ${item}`;
 * }
 *
 * for await (const result of runConcurrentlyStream({
 *   source: generateItems(),
 *   task: processItem,
 *   maxConcurrency: 10
 * })) {
 *   if (result.status === 'fulfilled') {
 *     console.log(result.value);
 *   } else {
 *     console.error('Failed:', result.reason);
 *   }
 * }
 */
export async function* runConcurrentlyStream<T, R>(
  options: RunConcurrentlyStreamOptions<T, R>
): AsyncIterable<StreamResult<T, R>> {
  const { source, task, maxConcurrency } = options;

  const limit = pLimit(maxConcurrency);

  // Wrap the task with error handling and concurrency limiting
  const wrappedTask = (item: T, index: number) =>
    limit(async (): Promise<StreamResult<T, R>> => {
      try {
        const value = await task(item, index);
        return {
          status: "fulfilled",
          value,
          item,
          index,
        };
      } catch (error) {
        console.error(`runConcurrentlyStream: Task failed at index ${index}:`, error);
        return {
          status: "rejected",
          reason: error instanceof Error ? error : new Error(String(error)),
          item,
          index,
        };
      }
    });

  const inFlight = new Map<Promise<StreamResult<T, R>>, StreamResult<T, R> | null>();
  let index = 0;

  for await (const item of source) {
    const currentIndex = index++;
    const promise = wrappedTask(item, currentIndex);
    inFlight.set(promise, null);

    // Store result when promise completes
    promise.then(
      (result) => {
        inFlight.set(promise, result);
        return result;
      },
      (error) => {
        console.error("Unexpected error in promise handler:", error);
      }
    );

    // If at capacity, wait for at least one to complete before continuing
    if (inFlight.size >= maxConcurrency) {
      await Promise.race(inFlight.keys());
      // Find and yield the completed promise
      for (const [p, result] of inFlight) {
        if (result !== null) {
          inFlight.delete(p);
          yield result;
          break;
        }
      }
    }
  }

  // Wait for all remaining promises and yield their results
  while (inFlight.size > 0) {
    await Promise.race(inFlight.keys());
    // Find and yield completed promises
    for (const [p, result] of inFlight) {
      if (result !== null) {
        inFlight.delete(p);
        yield result;
        break;
      }
    }
  }
}
